import React, { FC, useEffect, useMemo, useState } from 'react';
import 'reactflow/dist/style.css';
import styles from './productFlowMap.module.scss';
import { DataMappingProps } from 'enums/CreateProductStageNavigationBarEnums';
import { ProductRawMaterial } from 'models/ProductRawMaterial/productRawMaterial.model';
import { ProductRawMaterialTransportations } from 'models/ProductRawMaterialTransportations/productRawMaterialTransportations.model';
import GetReactFlow from '../GetReactFlow';
import { getNodeCards } from './productFlowMap.util';
import { ExpandIconState } from 'shared/components/RawMaterialMapColumnCard';
import { NodeMouseHandler } from 'reactflow';
import { ProductMapRoots, columnNodeKeys } from 'enums/ProductFlowMapEnums';
import clsx from 'clsx';
import { FlowNode, NodeLabels, NodesState } from './productFlowMap.types';
import { ProcessingFacilityEmissionData } from 'models/ProcessingFacilityEmissionData/ProcessingFacilityEmissionData.model';
import { ProductDistributionEmissionData } from 'models/ProductDistributionEmissionData/ProductDistributionEmissionData.model';

interface ProductFlowMapProps {
  stage1Emissions?: ProductRawMaterial[];
  stage2Emissions?: ProductRawMaterialTransportations[];
  stage3Emissions?: ProcessingFacilityEmissionData;
  stage4Emissions?: ProductDistributionEmissionData[];
  isDownloadPage?: boolean;
  setProductOverviewLoader?: React.Dispatch<React.SetStateAction<boolean>>;
  rootId: ProductMapRoots;
  hideColor?: boolean;
}

const containerId = 'product-flow-map'; // To set up wrapper and configure height and width
const TOTAL_COLUMNS = 4; //Based on the number of stages
export const DEFAULT_NODE_HEIGHT = 52.8;

const ProductFlowMap: FC<ProductFlowMapProps> = props => {
  const {
    stage1Emissions,
    stage2Emissions,
    stage3Emissions,
    stage4Emissions,
    isDownloadPage,
    setProductOverviewLoader,
    rootId,
    hideColor = false,
  } = props;

  const container = document.getElementById(containerId);
  const containerWidth = container ? container.offsetWidth : 0;

  const columnsData = [
    {
      emissionType: DataMappingProps.RAW_MATERIAL,
      emissions: stage1Emissions ?? [],
    },
    {
      emissionType: DataMappingProps.TRANSPORTATION,
      emissions: stage2Emissions ?? [],
    },
    {
      emissionType: DataMappingProps.PROCESSING_FACILITY,
      emissions: stage3Emissions ?? {},
    },
    {
      emissionType: DataMappingProps.PRODUCT_DISTRIBUTION,
      emissions: stage4Emissions ?? [],
    },
  ];

  //Initialising nodeComponents for each stage using getNodeCards()
  const initializeNodes = () => {
    const initialNodes: NodesState = {};
    for (let i = 0; i < TOTAL_COLUMNS; i++) {
      initialNodes[`col${i + 1}Nodes`] = getNodeCards({
        emissionType: columnsData[i].emissionType,
        emissions: columnsData[i].emissions,
        isDownload: isDownloadPage,
        hideColor: hideColor,
      });
    }
    return initialNodes;
  };

  const [nodes, setNodes] = useState<NodesState>(initializeNodes());

  useMemo(() => {
    setNodes(initializeNodes());
  }, [stage1Emissions, stage2Emissions, stage3Emissions, stage4Emissions]);

  //Nodes are set up again since the first time height of nodes will be 0 since element would not have been rendered during the first time
  useEffect(() => {
    setTimeout(() => {
      const hasZeroHeightNode = Object.values(nodes).some(column =>
        column.some(
          node =>
            ((node.isParentExpanded && node.isSubNode) || !node.isSubNode) &&
            node.height === 0
        )
      );

      if (hasZeroHeightNode || !containerWidth) {
        setNodes(initializeNodes());
      }
      setProductOverviewLoader?.(false);
    }, 150);
  }, [nodes]);

  // Container Height is changed after every time nodes are changed because height of the node is not fixed and independent based on the content
  const containerHeight = useMemo(() => {
    return (
      Object.values(nodes).reduce((maxHeight, nodeArray) => {
        const sumOfHeights = nodeArray
          .filter(item => !(item.isSubNode && !item.isParentExpanded))
          .reduce((sum, node) => sum + (node.height || 0) + 15, 0);
        return Math.max(maxHeight, sumOfHeights);
      }, 100) + 100
    );
  }, [nodes]);

  const toggleNodeVisibility = (node: NodeLabels, isVisible: boolean) => ({
    ...node,
    isSubNodesVisible: isVisible,
    label: {
      ...node.label,
      expandIcon: isVisible ? ExpandIconState.OPEN : ExpandIconState.CLOSED,
    },
  });

  const toggleParentExpandedState = (
    node: NodeLabels,
    isExpanded: boolean
  ) => ({
    ...node,
    isParentExpanded: isExpanded,
    height: DEFAULT_NODE_HEIGHT,
  });

  const updateNodesForKey = (
    nodes: NodesState,
    key: columnNodeKeys,
    customNodeId: string
  ) => {
    let index;
    return {
      newNodes: nodes[key].map((item, itemIndex) => {
        if (
          String(item.id) === customNodeId &&
          !item.isSubNode &&
          'expandIcon' in item.label
        ) {
          const newVisibility = !item.isSubNodesVisible;
          index = itemIndex + 1;
          return toggleNodeVisibility(item, newVisibility);
        }

        if (String(item.parentId) === customNodeId && item.isSubNode) {
          const newExpandedState = !item.isParentExpanded;
          return toggleParentExpandedState(item, newExpandedState);
        }

        return item;
      }),
      index: index,
    };
  };

  const updateNodesWithIndex = (nodes: NodeLabels[], index: number) => {
    let parentId = '';
    return nodes.map((item, itemIndex) => {
      if (
        itemIndex + 1 == index &&
        !item.isSubNode &&
        'expandIcon' in item.label
      ) {
        const newVisibility = !item.isSubNodesVisible;
        parentId = String(item.id);
        return toggleNodeVisibility(item, newVisibility);
      }

      if (parentId && String(item.parentId) === parentId && item.isSubNode) {
        const newExpandedState = !item.isParentExpanded;
        return toggleParentExpandedState(item, newExpandedState);
      }

      return item;
    });
  };

  const handleNodeClick: NodeMouseHandler = (_, node) => {
    const { id, columnNumber, isParent, hasSubNodes } = node as FlowNode;
    if (!isParent || columnNumber > 2 || !hasSubNodes || isDownloadPage) return;
    setNodes(prevNodes => {
      const updatedNodes: NodesState = { ...prevNodes };

      Object.keys(prevNodes).forEach(key => {
        if (
          (columnNumber == 1 && key === columnNodeKeys.COLUMN1) ||
          (columnNumber == 2 && key === columnNodeKeys.COLUMN2)
        ) {
          const { newNodes, index } = updateNodesForKey(prevNodes, key, id);
          updatedNodes[key] = newNodes;
          const alternateColumn =
            columnNumber === 1
              ? columnNodeKeys.COLUMN2
              : columnNodeKeys.COLUMN1;
          if (!!index)
            updatedNodes[alternateColumn] = updateNodesWithIndex(
              prevNodes[alternateColumn],
              index
            );
        }
      });
      return updatedNodes;
    });
  };

  useEffect(() => {
    const root = document.getElementById(rootId);
    const component = document.getElementById(containerId);
    if (!root) return;
    if (!component) return;
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      root.scrollBy({
        top: event.deltaY,
      });
    };
    component.addEventListener('wheel', handleWheel);
    return () => {
      component.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <>
      {/* Flow map can't be rendered without height and width to wrapper, width
      configured in CSS, height in inline styling since height is configured
      based on the individual node's heights */}
      <div
        className={clsx({
          [styles.productFlowMapWrapper]: true,
          [styles.minWidthWrapper]: hideColor,
        })}
        style={{
          // Default height of 100vh for first time render
          height: `${containerHeight ? `${containerHeight}px` : '100vh'}`,
        }}
        id={containerId}>
        <GetReactFlow
          containerHeight={containerHeight}
          containerWidth={containerWidth}
          nodesComponents={nodes}
          handleNodeClick={handleNodeClick}
          isDownload={isDownloadPage}
        />
      </div>
    </>
  );
};

export default ProductFlowMap;
